import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, XCircle, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Pricing = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <SEOHead title="AI Automation Pricing for Small Business | Xentic Solutions" description="$150 one-time setup. 3 months free. Then $50/mo. No hidden fees, no contracts, 30-day money-back guarantee." path="/pricing" />

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-1/3 -left-32 w-96 h-96 bg-primary/15 rounded-full blur-[120px] animate-blob" />
        <div className="container text-center max-w-3xl relative z-10">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Pricing</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold">Honest Pricing. <span className="text-gradient">No Hidden Fees.</span></h1>
            <p className="mt-6 text-lg text-muted-foreground">Most agencies charge $2,000 to $10,000 for what we deliver at $150. We believe automation should be accessible to every business.</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Main Pricing Card */}
      <section className="py-12">
        <div className="container max-w-lg">
          <ScrollReveal>
            <Card className="border-primary/30 glow-shadow-sm">
              <CardContent className="p-8 text-center">
                <h2 className="font-heading text-2xl font-bold mb-2">AI Chatbot Setup</h2>
                <div className="text-5xl font-heading font-bold text-primary mb-2">$150</div>
                <p className="text-sm text-muted-foreground mb-6">One-time setup fee</p>
                <ul className="text-left space-y-3 mb-8">
                  {[
                    "Custom AI chatbot trained on your business",
                    "Website, Social Media, WhatsApp & SMS fully integrated",
                    "Responses in under 2 seconds, 24/7",
                    "Automated lead capture, qualification & booking",
                    "Smart objection handling & FAQ automation",
                    "3 Months Free Customer Support",
                    "30 Day Money Back Guarantee",
                    "Launch in 72 Hours, no downtime, no missed leads",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground mb-6">Optional Add-On: Dedicated Bot Support Manager <span className="font-bold text-foreground">$50/mo</span> (after first 3 months)</p>
                <Button
                  size="lg"
                  className="w-full border-0"
                  style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                  onClick={() => navigate("/contact")}
                >
                  <span className="text-white font-semibold">Get Started for $150 →</span>
                </Button>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </section>

      {/* Tools Cost Breakdown */}
      <section className="py-20 section-alt">
        <div className="container max-w-2xl">
          <ScrollReveal>
            <h2 className="font-heading text-2xl font-bold text-center mb-6">Transparent Tools Cost Breakdown</h2>
            <p className="text-center text-muted-foreground mb-8">We pass through third-party tool costs at cost, no markup.</p>
            <div className="space-y-3">
              {[
                { tool: "ChatGPT API (OpenAI)", cost: "~$20/mo" },
                { tool: "Whatbot (WhatsApp integration)", cost: "~$25/mo" },
              ].map((t, i) => (
                <div key={i} className="flex items-center justify-between glass-card rounded-lg p-4">
                  <span className="text-sm font-medium">{t.tool}</span>
                  <span className="text-sm text-muted-foreground">{t.cost}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-6 text-center">These are your direct subscriptions. We don't touch them. You see exactly what you're paying for.</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20">
        <div className="container max-w-2xl">
          <ScrollReveal>
            <h2 className="font-heading text-2xl font-bold text-center mb-8">Xentic vs. Typical Agency</h2>
            <div className="rounded-xl overflow-hidden glass-card -mx-2 md:mx-0">
              <table className="w-full text-xs md:text-sm">
                <thead><tr style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}><th className="p-2 md:p-4 text-left text-white">Feature</th><th className="p-2 md:p-4 text-center text-white">Xentic</th><th className="p-2 md:p-4 text-center text-white">Typical Agency</th></tr></thead>
                <tbody>
                  {[
                    ["Setup Cost", "$150", "$2,000 to $10,000"],
                    ["Monthly Cost", "monthly_special", "$200 to $1,000/mo"],
                    ["Setup Time", "72 to 120 hrs", "2 to 6 weeks"],
                    ["Money-Back Guarantee", true, false],
                    ["Transparent Pricing", true, false],
                  ].map(([f, x, c], i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-secondary/30" : ""}>
                      <td className="p-2 md:p-4 font-medium">{f as string}</td>
                      <td className="p-2 md:p-4 text-center">{typeof x === "boolean" ? <CheckCircle className="h-5 w-5 text-green-400 mx-auto" /> : x === "monthly_special" ? <span className="font-semibold text-primary text-center"><span className="block">$50/month</span><span className="block text-xs text-muted-foreground font-normal mt-0.5">Optional after 3 months. Includes dedicated support.</span></span> : <span className="font-semibold text-primary whitespace-pre-line">{x}</span>}</td>
                      <td className="p-2 md:p-4 text-center">{typeof c === "boolean" ? <XCircle className="h-5 w-5 text-destructive mx-auto" /> : <span className="text-muted-foreground">{c}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Zero Risk */}
      <section className="py-20 section-alt">
        <div className="container max-w-2xl text-center">
          <ScrollReveal>
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">Zero Risk Guarantee</h2>
            <p className="text-muted-foreground mb-6">If you're not satisfied within 30 days, we'll refund your setup fee. No questions asked. We take the risk so you don't have to.</p>
            <Button
              size="lg"
              className="border-0 hover:-translate-y-0.5 transition-transform duration-300"
              style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
              onClick={() => navigate("/contact")}
            >
              <span className="text-white font-semibold">Get Started for $150 →</span>
            </Button>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Pricing;
